import styles from './input.scss?inline';
import slotStyles from './input.slot.scss?inline';
import { LitElement, html, unsafeCSS, CSSResult } from 'lit';
import { property, queryAssignedElements } from 'lit/decorators.js';
import { literal } from 'lit/static-html.js';
import { C11nPrefix, C11nPrefixLiteral } from 'virtual:c11n-prefix';
import { appendStylesheet, c11nElement } from '../core';

export type InputSize = 'medium' | 'large';

@c11nElement()
export class InputComponent extends LitElement {
  static selector = `${C11nPrefix}-input`;
  static literal = literal`${C11nPrefixLiteral}-input`;
  static override styles: CSSResult = unsafeCSS(styles);

  // Some attributes (error) were being stripped in Stencil apps,
  // but we found a simple workaround. If using @property 
  // we may need to differentiate the name from the attribute 
  // https://github.com/ionic-team/stencil/issues/2703

  @property({ attribute: 'error' , type: Boolean })
  _error?: boolean;
  
  @property({ attribute: 'size', type: String, converter: (value: string | null) => value ?? 'medium' })
  _size: InputSize = 'medium';

  @property({ attribute: 'c11n-web-input', type: Boolean, reflect: true })
  readonly _c11n_web_input: boolean = true;

  @queryAssignedElements({ slot: 'label', selector: 'label' })
  labelEls!: Array<HTMLLabelElement>;

  @queryAssignedElements({ slot: 'input', selector: 'input' })
  inputEls!: Array<HTMLInputElement>;

  @queryAssignedElements({ slot: 'hint-error', selector: '[c11n-web-hint-error]' })
  hintErrorEls!: Array<HTMLElement>;

  get labelEl() {
    return this.labelEls && this.labelEls[0];
  }

  get inputEl() {
    return this.inputEls && this.inputEls[0];
  }

  get hintErrorEl() {
    return this.hintErrorEls && this.hintErrorEls[0];
  }

  get inputId() {
    return this.inputEl?.id;
  }

  override connectedCallback(): void {
    super.connectedCallback();
    this.appendStyles();

    setTimeout(() => {
      this.updateHintErrorError();
      this.updateHintErrorSize();
      this.updateSlotAttributes();
    });
  }

  appendStyles() {
    const slotStylesheet: string = slotStyles.toString()
      .replaceAll(/\bc11n-web-input\b/g, this.tagName.toLowerCase());
    appendStylesheet({ 
      target: this, 
      styles: slotStylesheet, 
      name: `${this.tagName.toLowerCase()}-slot`
    });
  }

  override attributeChangedCallback(name: string, _old: string | null, value: string | null) {
    super.attributeChangedCallback(name, _old, value);
    this.updateSlotAttributes();
  }

  updateSlotAttributes() {
    if (this.inputId) {
      this.labelEl?.setAttribute('for', this.inputId);
      this.inputEl.setAttribute('id', this.inputId);
      if (this.hintErrorEl) {
        this.hintErrorEl.setAttribute('id', `${this.inputId}-description`);
        this.inputEl.setAttribute('aria-describedby', `${this.inputId}-description`);
      }
    }
  }

  updateHintErrorError() {
    if (this._error) {
      this.hintErrorEl?.setAttribute('error', '');
    } else {
      this.hintErrorEl?.removeAttribute('error');
    }
  };

  updateHintErrorSize() {
    switch(this._size) {
      case null:
      case 'medium':
        this.hintErrorEl?.removeAttribute('size');
        break;
      case 'large':
        this.hintErrorEl?.setAttribute('size', 'large');
        break;
    }
  };

  override updated(changedProperties: Map<any, any>): void {
    if (changedProperties.has('_error')) {
      this.updateHintErrorError();
    }
    if (changedProperties.has('_size')) {
      this.updateHintErrorSize();
    }
  }

  override render() {
    return html`
    <slot name="label" @slotchange="${this.updateSlotAttributes}"></slot>
    <slot name="input" @slotchange="${this.updateSlotAttributes}"></slot>
    <slot name="hint-error"></slot>`;
  }
}
export const C11nInput = InputComponent.selector;
export const C11nInputLiteral = InputComponent.literal;
